import { ArrowRight } from "lucide-react";
import { RecordContextProvider, useGetList, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { contactFullName } from "../contacts/contactModel";
import { formatDate } from "../misc/formatDate";
import { ContractTypeName } from "../sponsorings/ContractTypeName";
import type { Contact, Sponsoring } from "../types";
import { daysBetweenIso, SOON_DAYS, HORIZON_DAYS } from "./dashboardFilters";
import { useDashboardFilters } from "./useDashboardFilters";

const EXPIRED_ROWS = 3;
const ENDING_SOON_ROWS = 5;

/** Expired sponsorings: a few rows, a summary of the rest, a link to all. */
export const ExpiredCard = () => {
  const translate = useTranslate();
  const { filters } = useDashboardFilters();
  const {
    data = [],
    total = 0,
    isPending,
    error,
  } = useGetList<Sponsoring>("sponsorings", {
    filter: filters.expired,
    sort: { field: "end_date", order: "DESC" },
    pagination: { page: 1, perPage: 100 },
  });
  const shown = data.slice(0, EXPIRED_ROWS);

  return (
    <DashboardCard
      title={translate("crm.dashboard.expired.title")}
      to="/sponsorings?filter=expired"
      linkLabel={translate("crm.dashboard.view_all_count", { count: total })}
      testId="expired-card"
    >
      <CardBody
        isPending={isPending}
        error={!!error}
        empty={data.length === 0}
        emptyText={translate("crm.dashboard.expired.empty")}
      >
        <ul className="divide-y divide-border">
          {shown.map((sponsoring) => (
            <SponsoringRow
              key={sponsoring.id}
              sponsoring={sponsoring}
              badge={translate("crm.dashboard.expiring.badge_expired")}
              badgeClassName="border-transparent bg-destructive text-white"
            />
          ))}
        </ul>
      </CardBody>
    </DashboardCard>
  );
};

/** Running sponsorings that end within the horizon, soonest first. */
export const EndingSoonCard = () => {
  const translate = useTranslate();
  const { filters, today } = useDashboardFilters();
  const {
    data = [],
    total = 0,
    isPending,
    error,
  } = useGetList<Sponsoring>("sponsorings", {
    filter: filters.within90,
    sort: { field: "end_date", order: "ASC" },
    pagination: { page: 1, perPage: ENDING_SOON_ROWS },
  });

  return (
    <DashboardCard
      title={translate("crm.dashboard.ending_soon.title")}
      to="/sponsorings?filter=ending_soon"
      linkLabel={translate("crm.dashboard.view_all_count", { count: total })}
      testId="ending-soon-card"
    >
      <CardBody
        isPending={isPending}
        error={!!error}
        empty={data.length === 0}
        emptyText={translate("crm.dashboard.expiring.empty")}
      >
        <ul className="divide-y divide-border">
          {data.map((sponsoring) => {
            const days = sponsoring.end_date
              ? daysBetweenIso(today, sponsoring.end_date)
              : HORIZON_DAYS;
            const soon = days <= SOON_DAYS;
            return (
              <SponsoringRow
                key={sponsoring.id}
                sponsoring={sponsoring}
                badge={translate("crm.dashboard.ending_soon.within", {
                  days: soon ? SOON_DAYS : HORIZON_DAYS,
                })}
                badgeClassName={
                  soon
                    ? "border-transparent bg-orange-100 text-orange-800"
                    : "border-transparent bg-amber-100 text-amber-800"
                }
              />
            );
          })}
        </ul>
      </CardBody>
    </DashboardCard>
  );
};

const DashboardCard = ({
  title,
  to,
  linkLabel,
  testId,
  children,
}: {
  title: string;
  to: string;
  linkLabel: string;
  testId: string;
  children: ReactNode;
}) => (
  <section
    className="flex flex-col rounded-xl border border-border bg-white p-4 md:p-5"
    data-testid={testId}
  >
    <div className="mb-2 flex items-center justify-between gap-3 border-b border-border pb-3">
      <h2 className="min-w-0 truncate text-lg font-bold text-brand-navy">
        {title}
      </h2>
      <Link
        to={to}
        className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-brand-blue no-underline hover:underline"
      >
        {linkLabel}
        <ArrowRight className="size-4" />
      </Link>
    </div>
    {children}
  </section>
);

const CardBody = ({
  isPending,
  error,
  empty,
  emptyText,
  children,
}: {
  isPending: boolean;
  error: boolean;
  empty: boolean;
  emptyText: string;
  children: ReactNode;
}) => {
  const translate = useTranslate();
  if (isPending) {
    return (
      <div className="flex flex-col gap-3 py-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="py-3 text-sm text-destructive">
        {translate("crm.dashboard.error")}
      </p>
    );
  }
  if (empty) {
    return <p className="py-3 text-sm text-muted-foreground">{emptyText}</p>;
  }
  return <>{children}</>;
};

const SponsoringRow = ({
  sponsoring,
  badge,
  badgeClassName,
}: {
  sponsoring: Sponsoring;
  badge: string;
  badgeClassName: string;
}) => {
  const { data: contacts } = useGetList<Contact>("contacts", {
    filter: { company_id: sponsoring.company_id },
    sort: { field: "id", order: "ASC" },
    pagination: { page: 1, perPage: 1 },
  });
  const contact = contacts?.[0];

  return (
    <li>
      <RecordContextProvider value={sponsoring}>
        <Link
          to={`/sponsorings/${sponsoring.id}/show`}
          className="flex items-center gap-3 py-3 text-foreground no-underline transition-colors hover:bg-muted/50 md:gap-4"
          data-testid={`expiring-sponsoring-${sponsoring.id}`}
        >
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">
              <ReferenceField
                source="company_id"
                reference="companies"
                link={false}
                loading={<Skeleton className="h-4 w-32" />}
              />
            </div>
            <div className="truncate text-sm text-muted-foreground">
              <ContractTypeName />
              {" · "}
              {contact ? contactFullName(contact) : "—"}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1 md:flex-row md:items-center md:gap-4">
            <span className="text-sm text-muted-foreground tabular-nums">
              {sponsoring.end_date ? formatDate(sponsoring.end_date) : ""}
            </span>
            <Badge
              variant="outline"
              className={cn("shrink-0 font-semibold", badgeClassName)}
            >
              {badge}
            </Badge>
          </div>
        </Link>
      </RecordContextProvider>
    </li>
  );
};
